<template>
  <card card-body-classes="table-full-width">
    <div slot="header" class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap">
      <h4 slot="header" class="card-title">{{ $t('accountPurchase.mainView.title') }}</h4>
    </div>
    <div>
      <div
        class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
      >
        <el-select
          class="select-primary mb-3 pagination-select"
          v-model="pagination.perPage"
          placeholder="Per page"
        >
          <el-option
            class="select-primary"
            v-for="item in pagination.perPageOptions"
            :key="item"
            :label="item"
            :value="item"
          >
          </el-option>
        </el-select>

        <div class="row">
          <div class="col-6">
            <base-input>
              <el-input
                type="search"
                class="mb-3 search-input"
                prefix-icon="el-icon-search"
                :placeholder="$t('generic.searchInTablePlaceholder')"
                v-model="searchQuery"
                aria-controls="datatables"
              >
              </el-input>
            </base-input>
          </div>

        </div>
      </div>

      <el-table v-loading="loading" :data="queriedData" :row-class-name="tableRowClassName" :empty-text="$t('generic.noData')">
        <el-table-column type="expand">
          <template slot-scope="props">
            <purchase-details :details="props.row"/>
          </template>
        </el-table-column>
        <el-table-column
          v-for="column in tableColumns"
          :key="column.label"
          :prop="column.prop"
          :label="$t(column.label)"
          :formatter="column.formatter"
        >
        </el-table-column>
        <el-table-column align="right" width="50">
          <div slot-scope="props">
            <router-link
              :to="{name: 'account-purchase-details', params: {id: props.row.account_id, resource_id: props.row.id}}">
              <i class="tim-icons icon-double-right"></i>
            </router-link>
          </div>
        </el-table-column>
      </el-table>
    </div>
    <div
      slot="footer"
      class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
    >
      <div class="">
        <p class="card-category">
          {{ $t('generic.tablePaginationSummary', {from: from + 1, to: to, total: total}) }}
        </p>
      </div>
      <base-pagination
        class="pagination-no-border"
        v-model="pagination.currentPage"
        :per-page="pagination.perPage"
        :total="total"
      >
      </base-pagination>
    </div>
  </card>
</template>

<script>
import {Option, Select, Table, TableColumn} from 'element-ui';
import {BaseAlert, BasePagination} from 'src/components';
import Fuse from 'fuse.js';
import {mapActions, mapState} from "vuex";
import PurchaseDetails from 'src/components/Purchase/PurchaseDetails'
import account_purchases from "@/store/modules/account_purchases";

export default {
  name: 'accountPurchases',
  components: {
    BasePagination,
    BaseAlert,
    [Select.name]: Select,
    [Option.name]: Option,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    PurchaseDetails
  },
  methods: {
    ...mapActions(['GetAccountPurchases']),
    tableRowClassName({row}) {
      if (row["status"] === "failed") {
        return 'bg-warning';
      }
      return '';
    },
  },
  computed: {
    ...mapState({
      loading: state => state.account_purchases.loading,
      data: state => state.account_purchases.data,
    }),
    /***
     * Returns a page from the searched data or the whole data. Search is performed in the watch section below
     */
    queriedData() {
      let result = this.tableData;
      if (this.searchedData.length > 0) {
        result = this.searchedData;
      }
      return result.slice(this.from, this.to);
    },
    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },
    total() {
      return this.searchedData.length > 0
        ? this.searchedData.length
        : this.tableData.length;
    }
  },
  data() {
    return {
      pagination: {
        perPage: 50,
        currentPage: 1,
        perPageOptions: [5, 10, 25, 50],
        total: 0
      },
      searchQuery: '',
      propsToSearch: ['amount', 'created_at', 'processed_at', 'status'],
      tableColumns: [
        {
          prop: 'created_at',
          label: 'generic.created',
          formatter: (row, column, cellValue, index) => {
            return this.$options.filters.time(cellValue)
          },
        },
        {
          prop: 'updated_at',
          label: 'generic.updated',
          formatter: (row, column, cellValue, index) => {
            return this.$options.filters.time(cellValue)
          },
        },
        {
          prop: 'operation_ts',
          label: 'purchaseDetails.operation_ts',
          formatter: (row, column, cellValue, index) => {
            return this.$options.filters.time(cellValue)
          },
        },
        {
          prop: 'product_type',
          label: 'purchaseDetails.product_type',
        },
        {
          prop: 'discount_id',
          label: 'purchaseDetails.discount_id',
        },
        {
          prop: 'charge',
          label: 'purchaseDetails.charge',
          formatter: (row, column, cellValue, index) => {
            return this.$options.filters.price(cellValue)
          }
        },
        {
          prop: 'fare_group_id',
          label: 'purchaseDetails.fare_group_id',
        },
        {
          prop: 'operator_id',
          label: 'purchaseDetails.operator_id',
        },
      ],
      tableData: [],
      searchedData: [],
      fuseSearch: null
    };
  },
  mounted() {
    this.GetAccountPurchases(this.$route.params.id)
  },
  watch: {
    /**
     * Searches through the table data by a given query.
     * NOTE: If you have a lot of data, it's recommended to do the search on the Server Side and only display the results here.
     * @param value of the query
     */
    searchQuery(value) {
      let result = this.tableData;
      if (value !== '') {
        result = this.fuseSearch.search(this.searchQuery);
      }
      this.searchedData = result;
    },
    data(data) {
      this.tableData = data;
      // Fuse search initialization.
      this.fuseSearch = new Fuse(this.tableData, {
        keys: ['ticket_type', 'fare_group_id'],
        threshold: 0.3
      });
    }
  }
};
</script>
